import { useId } from "react";

export type CompetitorPanelOverlayStatus = "waiting" | "prepping" | "analyzing" | "matching" | "creating";
type DeterminateScanningStep = Exclude<CompetitorPanelOverlayStatus, "waiting" | "prepping">;

export interface ScanningProgress {
      step: DeterminateScanningStep;
      current: number;
      total: number;
}

interface ScanningStepIndicatorProps {
      status: CompetitorPanelOverlayStatus;
      progress: ScanningProgress | null;
}

const STATUS_COPY: Record<CompetitorPanelOverlayStatus, string> = {
      waiting: "Waiting",
      prepping: "Prepping",
      analyzing: "Analyzing competitors",
      matching: "Matching competitors",
      creating: "Creating competitors",
};

const getProgressPercentage = (
      status: CompetitorPanelOverlayStatus,
      progress: ScanningProgress | null,
): number | null => {
      if ((status !== "analyzing" && status !== "creating") || !progress) {
            return null;
      }

      if (progress.total <= 0) {
            return null;
      }

      return Math.min(100, Math.round((progress.current / progress.total) * 100));
};

export const ScanningStepIndicator = ({ status, progress }: ScanningStepIndicatorProps) => {
      const percentage = getProgressPercentage(status, progress);
      const shimmerId = useId().replace(/:/g, "");

      return (
            <div className="flex flex-col items-center gap-2.5">
                  {/* Pulsing dot + label */}
                  <div className="flex items-center gap-2">
                        <span className="relative flex h-1.5 w-1.5">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary-foreground/40 duration-[2000ms]" />
                              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-secondary-foreground/70" />
                        </span>
                        <span className="text-xs font-medium tracking-wide text-foreground/70">
                              {STATUS_COPY[status]}
                        </span>
                  </div>

                  {/* Thin progress track */}
                  <div className="w-16 overflow-hidden rounded-full">
                        {percentage !== null ? (
                              <div className="h-[3px] w-full rounded-full bg-border/50">
                                    <div
                                          className="h-full rounded-full bg-secondary-foreground/45 transition-all duration-700 ease-out"
                                          style={{ width: `${percentage}%` }}
                                    />
                              </div>
                        ) : (
                              <div className="h-[3px] w-full rounded-full bg-border/30">
                                    <svg width="100%" height="3" className="overflow-visible" aria-hidden="true">
                                          <defs>
                                                <linearGradient id={shimmerId} x1="0" y1="0" x2="1" y2="0">
                                                      <stop
                                                            offset="0%"
                                                            stopColor="var(--secondary-foreground)"
                                                            stopOpacity="0"
                                                      />
                                                      <stop
                                                            offset="50%"
                                                            stopColor="var(--secondary-foreground)"
                                                            stopOpacity="0.45"
                                                      />
                                                      <stop
                                                            offset="100%"
                                                            stopColor="var(--secondary-foreground)"
                                                            stopOpacity="0"
                                                      />
                                                </linearGradient>
                                          </defs>
                                          <rect
                                                width="40%"
                                                height="3"
                                                rx="1.5"
                                                fill={`url(#${shimmerId})`}
                                                className="animate-shimmer-slide"
                                          />
                                    </svg>
                              </div>
                        )}
                  </div>
            </div>
      );
};
